// ⬡B:msria-home.app:SURFACE:public_glass_landing_signin_door:20260604⬡
// ⬡B:msria-home.app.ms_mac_import:CODE:ms_mac_persona_clone_fix:20260607⬡
// msria.org, the public face of Ms.Ria. Thin client: NO hardcoded identity.
// Shell (GlassStyles + smoke + glass panel) now live in @aba/ms-mac-core.
import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useHam, signInFailureMessage, signInUnavailableNotice } from '@aba/ham-core';
import { Shell } from '@aba/ms-mac-core';
import Chat from './Chat.jsx';
import CaraPortal from './CaraPortal.jsx';
import Schedule from './Schedule.jsx';

const TAGLINE = '#TheLifeAssistant';

function Landing() {
  const { ham, loading } = useHam();
  const nav = useNavigate();

  // Auth callback handler — Supabase Site URL is msria.org so all OAuth lands here.
  // Auto-redirect after session resolves: to ?from= URL (cross-origin) or /chat.
  useEffect(() => {
    if (loading || !ham) return;
    // Only auto-redirect when there is an EXPLICIT stored destination.
    // This fires after OAuth lands at msria.org/ (Supabase Site URL fallback).
    // Without an explicit destination the user sees the Landing and clicks through.
    // Unconditional nav('/chat') was wrong — it hijacked every surface.
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get('from');
    const fromStorage = sessionStorage.getItem('auth_return_to');
    if (fromStorage) sessionStorage.removeItem('auth_return_to');
    const dest = fromStorage || fromParam;
    if (!dest) return; // No destination stored — let user click the button
    if (dest.startsWith('https://tryaba.org') || dest.startsWith('https://msria.org/')) {
      window.location.replace(dest);
    }
  }, [ham, loading, nav]);

  return (
    <Shell>
      <h1 className="name">Ms<span className="dot">.</span>Ria</h1>
      <p className="tag">{TAGLINE}</p>
      <button className="cta" onClick={() => nav(ham ? '/chat' : '/join')} disabled={loading}>
        {loading ? '…' : ham ? 'Open Ms.Ria' : 'Meet Ms.Ria'}
      </button>
      <p className="sub"><Link to="/about">What is this?</Link></p>
    </Shell>
  );
}

// ⬡B:DC499D0C:FIX:sign_in_never_a_dead_button:20260801⬡
// TM-9 neighbour sweep. This door differed from the GMG U one: it DID catch. It
// then threw the catch away, `catch (_) { setBusy(false); }`, so a failed click put
// the button back exactly as it was and said nothing. Same outcome for the person
// standing there as no catch at all. Both doors now say one plain sentence.
function Meet() {
  const { signInWithGoogle, signInWithEmailLink, hasAuth } = useHam();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(() => signInUnavailableNotice({ hasAuth }));

  async function google() {
    setNotice(null);
    setBusy(true);
    try {
      if (typeof signInWithGoogle !== 'function') throw new Error('sign in unavailable');
      await signInWithGoogle();
    } catch (e) {
      setBusy(false);
      setNotice(signInFailureMessage(e));
    }
  }
  async function emailLink() {
    if (!email.trim()) return;
    setNotice(null);
    setBusy(true);
    try {
      if (typeof signInWithEmailLink !== 'function') throw new Error('sign in unavailable');
      await signInWithEmailLink(email.trim());
      setSent(true);
    } catch (e) {
      setNotice(signInFailureMessage(e));
    } finally { setBusy(false); }
  }

  if (sent) {
    return (
      <Shell>
        <h1 className="name">Check your inbox</h1>
        <p className="tag">A sign-in link is on its way to {email}. Open it on this device and Ms.Ria will be right here.</p>
        <Link className="back" to="/">Back</Link>
      </Shell>
    );
  }
  return (
    <Shell>
      <h1 className="name">Meet Ms<span className="dot">.</span>Ria</h1>
      <p className="tag">One door, your space behind it.</p>
      {notice && (
        <p
          role="status"
          style={{ color:'#ffd9a8', background:'rgba(120,53,15,0.5)', border:'1px solid rgba(226,177,76,0.35)', borderRadius:14, padding:'12px 18px', fontSize:14, lineHeight:1.6, margin:'0 0 18px', maxWidth:360, textAlign:'center' }}
        >{notice}</p>
      )}
      <button className="cta" onClick={google} disabled={busy}>{busy ? 'Opening…' : 'Continue with Google'}</button>
      <input className="field" type="email" inputMode="email" placeholder="or enter your email"
        value={email} onChange={(e) => setEmail(e.target.value)} />
      <button className="ghost" onClick={emailLink} disabled={busy || !email.trim()}>Email me a link</button>
      <Link className="back" to="/">Back</Link>
    </Shell>
  );
}

function Welcome() {
  const { ham, loading, unrecognized, signOut } = useHam();
  const nav = useNavigate();
  if (loading) return <Shell><p className="tag">One moment.</p></Shell>;
  if (!ham || unrecognized) {
    return (
      <Shell>
        <h1 className="name">Almost there</h1>
        <p className="tag">We could not find your space yet. Try the door once more.</p>
        <button className="cta" onClick={() => nav('/meet')}>Meet Ms.Ria</button>
      </Shell>
    );
  }
  const greetName = (ham && (ham.name || ham.firstName)) || 'there';
  return (
    <Shell>
      <h1 className="name">Welcome, {greetName}</h1>
      <p className="tag">Your space is ready. This is where the conversation opens, just for you.</p>
      <button className="cta" onClick={() => nav('/chat')}>Open Ms.Ria</button>
      <button className="ghost" onClick={signOut}>Sign out</button>
    </Shell>
  );
}

function About() {
  return (
    <Shell>
      <p className="lede">Ms.Ria is the life assistant.</p>
      <p className="body">Most assistants do one thing in one place. Ms.Ria holds the whole picture, the work and the home and the in-between, and keeps it in mind so you do not have to carry all of it yourself.</p>
      <p className="body">She remembers what matters to you, picks up where you left off, and meets you in plain words. No dashboards to learn, no settings to wrestle. You talk, she listens, things get handled.</p>
      <p className="body">Your space is yours alone. What you share with Ms.Ria stays inside your space, behind your own door.</p>
      <Link className="back" to="/">Back</Link>
    </Shell>
  );
}

function Intake() {
  const nav = useNavigate();
  const [mode, setMode] = useState('fork');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const API = (import.meta.env && import.meta.env.VITE_ABA_API_BASE) || 'https://ababase.onrender.com';

  async function join() {
    const e = email.trim();
    if (!e || !e.includes('@')) { setErr('Please enter a valid email.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(API + '/api/waitlist/join', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e, name: name.trim() }),
      });
      const d = await r.json();
      if (d && d.existing) setMode('existing');
      else if (d && d.joined) setMode('joined');
      else setErr('Something went sideways. Try once more in a moment.');
    } catch (_) {
      setErr('I could not reach the list just now. One more try?');
    } finally { setBusy(false); }
  }

  if (mode === 'joined') return (
    <Shell>
      <h1 className="name">You are on the list</h1>
      <p className="tag">I have your spot saved. When your space is ready I will reach out, and we will pick it up from there.</p>
      <Link className="back" to="/">Back</Link>
    </Shell>
  );
  if (mode === 'existing') return (
    <Shell>
      <h1 className="name">You already have a space</h1>
      <p className="tag">Looks like we have met. Sign in and Ms.Ria will be right where you left her.</p>
      <button className="cta" onClick={() => nav('/meet')}>Sign in</button>
      <Link className="back" to="/">Back</Link>
    </Shell>
  );
  if (mode === 'form') return (
    <Shell>
      <h1 className="name">Join the list</h1>
      <p className="tag">Leave your email and Ms.Ria will open a space for you soon.</p>
      <input className="field" type="email" inputMode="email" placeholder="your email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input className="field" type="text" placeholder="your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
      {err ? <p className="tag" style={{ color: 'var(--rose)' }}>{err}</p> : null}
      <button className="cta" onClick={join} disabled={busy || !email.trim()}>Join the list</button>
      <button className="ghost" onClick={() => setMode('fork')} disabled={busy}>Back</button>
    </Shell>
  );
  return (
    <Shell>
      <h1 className="name">Ms<span className="dot">.</span>Ria</h1>
      <p className="tag">Are you new here, or do you already have a space?</p>
      <button className="cta" onClick={() => setMode('form')}>I am new, join the list</button>
      <button className="ghost" onClick={() => nav('/meet')}>I already have a space</button>
      <Link className="back" to="/">Back</Link>
    </Shell>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/meet" element={<Meet />} />
      <Route path="/join" element={<Intake />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/portal" element={<CaraPortal />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/about" element={<About />} />
      <Route path="/schedule/:uid" element={<Schedule />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}
